import React from 'react';
import { Infinity, Search, Package, Megaphone, DollarSign } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { FeatureCard } from '../components/ui/FeatureCard';

export const PerpetuumAiPage: React.FC = () => {
    usePageMetadata({
        title: "Perpetuum AI | System Pasywnego Dochodu z AI",
        description: "Zbuduj maszynę do generowania pasywnego dochodu. Perpetuum AI znajduje nisze, tworzy produkty cyfrowe i sprzedaje je 24/7. Zobacz, jak to działa.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Perpetuum AI (System Dochodu)",
            "serviceType": "Generowanie Pasywnego Dochodu",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Kompletny, samodoskonalący się system AI do budowy pasywnego dochodu. Znajduje nisze rynkowe, tworzy produkty cyfrowe, pozyskuje klientów i sprzedaje 24/7.",
            "url": "https://biznesbezklikania.pl/rozwiazania/perpetuum-ai"
        }
    });

    const steps = [
        { icon: <Search className="w-8 h-8" />, title: '1. Analiza Rynku', description: 'AI skanuje trendy, fora i media społecznościowe w poszukiwaniu niszowych problemów, za których rozwiązanie ludzie są gotowi zapłacić.' },
        { icon: <Package className="w-8 h-8" />, title: '2. Tworzenie Produktu', description: 'System automatycznie generuje produkty cyfrowe - e-booki, szablony, mini-kursy - które rozwiązują zidentyfikowany problem.' },
        { icon: <Megaphone className="w-8 h-8" />, title: '3. Pozyskiwanie Ruchu', description: 'AI tworzy i publikuje treści SEO i posty w social media, aby przyciągnąć docelową grupę klientów na stronę sprzedażową.' },
        { icon: <DollarSign className="w-8 h-8" />, title: '4. Automatyzacja Sprzedaży', description: 'Odpowiada na pytania, procesuje płatności i dostarcza produkt. System samodzielnie optymalizuje ceny i testuje oferty.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="default" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <RevealOnScroll>
                                <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                                    System Pasywnego Dochodu
                                </p>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                    Perpetuum AI
                                </h1>
                            </RevealOnScroll>

                            <RevealOnScroll delay={100}>
                                <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                    Maszyna do generowania pasywnego dochodu. Kompletny, samodoskonalący się system, który znajduje nisze rynkowe, tworzy produkty cyfrowe, pozyskuje klientów i sprzedaje 24/7.
                                </p>
                            </RevealOnScroll>

                            <RevealOnScroll delay={200}>
                                <LeadFormModal
                                    productSlug="perpetuum-ai"
                                    triggerLabel="Zbuduj swoją maszynę"
                                    triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                                />
                            </RevealOnScroll>
                        </div>

                        <RevealOnScroll delay={300} direction="right">
                            <div className="flex items-center justify-center">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 rounded-full blur-3xl" />
                                    <Infinity className="w-48 h-48 md:w-64 md:h-64 text-primary-light dark:text-primary-dark animate-pulse relative z-10" />
                                </div>
                            </div>
                        </RevealOnScroll>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* How It Works Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak Działa Perpetuum AI?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            To zamknięty cykl automatyzacji, który pracuje dla Ciebie, nawet gdy śpisz.
                        </p>
                    </RevealOnScroll>

                    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
                        {steps.map((step, index) => (
                            <RevealOnScroll key={step.title} delay={index * 100}>
                                <FeatureCard
                                    icon={step.icon}
                                    title={step.title}
                                    description={step.description}
                                    variant="horizontal"
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/perpetuum-ai" />
        </>
    );
};
